/**
 * Converts an EditorJS JSON string into clean plain text.
 * Used for description previews in job/portfolio cards where
 * we only need a text snippet, not full rendered HTML.
 *
 * If the content is not valid EditorJS JSON it is returned as-is.
 *
 * @param content  - Raw EditorJS JSON string (or plain text)
 * @param maxLength - If provided, truncates to this many characters
 */

/** Decode common HTML entities left after stripping tags */
function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    // Collapse multiple spaces/newlines that result from stripping tags
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function stripTagsAndDecode(html: string): string {
  return decodeHtmlEntities(html.replace(/<[^>]+>/g, ''));
}

export function editorJsToPlainText(
  content: string | null | undefined,
  maxLength?: number,
): string {
  if (!content) return '';

  let text = '';

  try {
    const parsed = JSON.parse(content) as { blocks?: Array<{ type: string; data: Record<string, unknown> }> };

    if (parsed && Array.isArray(parsed.blocks)) {
      const parts: string[] = [];

      for (const block of parsed.blocks) {
        switch (block.type) {
          case 'paragraph':
          case 'header': {
            const raw = (block.data.text as string) ?? '';
            parts.push(stripTagsAndDecode(raw));
            break;
          }
          case 'list': {
            const items = (block.data.items ?? []) as Array<string | { content: string }>;
            for (const item of items) {
              const raw = typeof item === 'string' ? item : (item.content ?? '');
              parts.push(stripTagsAndDecode(raw));
            }
            break;
          }
          case 'quote': {
            const raw = (block.data.text as string) ?? '';
            parts.push(stripTagsAndDecode(raw));
            break;
          }
          default:
            break;
        }

        // Early exit once we have enough text
        if (maxLength && parts.join(' ').length > maxLength) break;
      }

      text = parts.join(' ');
    }
  } catch {
    // Not JSON - use as-is (plain text), still decode entities
    text = decodeHtmlEntities(content);
  }

  if (!text) text = decodeHtmlEntities(content);

  if (maxLength && text.length > maxLength) {
    return text.slice(0, maxLength).trimEnd() + '…';
  }

  return text;
}
